<script setup>
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAdminCenterStore } from '../../stores/AdminCenterStore'
import { isAgencyAdminUser } from '../../access/roleKeys'
import { handleErrors } from '../../utils/errorHandler'
import BcsCard from '../common/BcsCard.vue'
import BcsStatPill from '../common/BcsStatPill.vue'
import BcsStatusPill from '../common/BcsStatusPill.vue'
import BcsDropdownActions from '../common/BcsDropdownActions.vue'
import AddAccountUserModal from './AddAccountUserModal.vue'
import ConfirmActionModal from './ConfirmActionModal.vue'
import RemoveUserModal from './RemoveUserModal.vue'

const adminCenterStore = useAdminCenterStore()
const {
  isLoading,
  isLoadingMoreActive,
  isLoadingMorePending,
  activeUsers,
  pendingUsers,
  totalActive,
  totalPending,
  hasMoreActive,
  hasMorePending
} = storeToRefs(adminCenterStore)

const emit = defineEmits(['showAlert'])

const activeTab = ref('active')
const addUserModalRef = ref(null)
const confirmModalRef = ref(null)
const removeUserModalRef = ref(null)
const userToRemove = ref(null)

function showAlert(message) {
  emit('showAlert', message)
}

// Confirmation modal state
const confirmModalTitle = ref('')
const confirmModalBody = ref('')
const confirmModalLabel = ref('Confirm')
const isConfirmProcessing = ref(false)
let pendingConfirmAction = null

function openConfirmModal({ title, body, confirmLabel, onConfirm }) {
  confirmModalTitle.value = title
  confirmModalBody.value = body
  confirmModalLabel.value = confirmLabel || 'Confirm'
  pendingConfirmAction = onConfirm
  isConfirmProcessing.value = false
  confirmModalRef.value.openModal()
}

async function onConfirmAction() {
  if (!pendingConfirmAction) return
  isConfirmProcessing.value = true
  try {
    await pendingConfirmAction()
  } finally {
    isConfirmProcessing.value = false
    confirmModalRef.value.closeModal()
    pendingConfirmAction = null
  }
}

function formatDate(value) {
  if (!value) return 'N/a'
  const date = value.toDate ? value.toDate() : new Date(value)
  if (isNaN(date.getTime())) return 'N/a'
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const currentUid = localStorage.getItem('userUid')

function getDropdownActions(user) {
  const isSelf = user.id === currentUid
  const isAlreadyAdmin = isAgencyAdminUser(user)
  return [
    {
      key: 'allowAdmin',
      label: 'Allow Admin Rights',
      disabled: isAlreadyAdmin,
      tooltipText: isAlreadyAdmin ? 'This user already has admin rights' : ''
    },
    {
      key: 'removeUser',
      label: 'Remove User',
      disabled: isSelf,
      tooltipText: isSelf ? 'You cannot remove your own account' : '',
      variant: 'danger'
    }
  ]
}

function getPendingDropdownActions() {
  return [
    { key: 'resendInvite', label: 'Send Invite Nudge' },
    { key: 'cancelInvite', label: 'Cancel Invite', variant: 'danger' }
  ]
}

function onDropdownAction(action, user) {
  if (action === 'allowAdmin') {
    openConfirmModal({
      title: 'Grant Admin Access',
      body: `Are you sure you want to grant admin rights to ${user.fullname}? This will allow them to access the Admin Center and manage users.`,
      confirmLabel: 'Grant Admin Rights',
      onConfirm: async () => {
        try {
          await adminCenterStore.grantAgencyAdmin(user.id)
          showAlert(`Admin rights granted to ${user.fullname} successfully!`)
        } catch (error) {
          handleErrors('ManageUsers.allowAdmin', error)
        }
      }
    })
  } else if (action === 'removeUser') {
    userToRemove.value = user
    removeUserModalRef.value.openModal()
  } else if (action === 'resendInvite') {
    openConfirmModal({
      title: 'Resend Invite',
      body: `Are you sure you want to resend the password creation email to ${user.fullname} (${user.email})?`,
      confirmLabel: 'Resend Invite',
      onConfirm: async () => {
        try {
          await adminCenterStore.resendInvite(user.id)
          showAlert(`Invite resent to ${user.email} successfully!`)
        } catch (error) {
          handleErrors('ManageUsers.resendInvite', error)
        }
      }
    })
  } else if (action === 'cancelInvite') {
    openConfirmModal({
      title: 'Cancel Invite',
      body: `Are you sure you want to cancel the invite for ${user.fullname} (${user.email})? This will permanently remove the user from the system.`,
      confirmLabel: 'Cancel Invite',
      onConfirm: async () => {
        try {
          await adminCenterStore.cancelInvite(user.id)
          showAlert(`Invite for ${user.fullname} has been cancelled successfully!`)
        } catch (error) {
          handleErrors('ManageUsers.cancelInvite', error)
        }
      }
    })
  }
}

async function onRemoveConfirm({ user, reassignTo }) {
  try {
    await adminCenterStore.deactivateUser(user.id, reassignTo)
    removeUserModalRef.value.closeModal()
    showAlert(`${user.fullname} has been removed successfully!`)
  } catch (error) {
    handleErrors('ManageUsers.removeUser', error)
    removeUserModalRef.value.setProcessing(false)
  }
}

function openAddUserModal() {
  addUserModalRef.value.openModal()
}

async function onInviteSubmit(formData) {
  try {
    await adminCenterStore.createUser(formData)
    addUserModalRef.value.closeModal()
    showAlert('Account invite sent successfully!')
  } catch (error) {
    if (error.code === 'user_deactivated') {
      addUserModalRef.value.setEmailError(error.message)
    } else if (error.code === 'Email already in use' || error.message === 'Email already in use') {
      addUserModalRef.value.setEmailError('This email is already associated with an existing account.')
    } else {
      handleErrors('ManageUsers.onInviteSubmit', error)
      addUserModalRef.value.closeModal()
    }
  }
}
</script>

<template>
  <BcsCard title="Manage Users">
    <template #header-right>
      <div class="d-flex align-items-center gap-2">
        <button
          class="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
          type="button"
          :disabled="isLoading"
          @click="adminCenterStore.refreshUsers()"
        >
          <i class="bi bi-arrow-clockwise d-flex py-1" :class="{ 'spin-animation': isLoading }"></i>
        </button>
        <button class="btn btn-primary btn-sm d-flex align-items-center gap-1" type="button" @click="openAddUserModal">
          <i class="bi bi-plus-square d-flex"></i>
          Add New Account User
        </button>
      </div>
    </template>

    <div class="px-3 pt-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs mb-0 border-0">
        <li class="nav-item">
          <button
            class="nav-link manage-users-tab text-16"
            :class="{ active: activeTab === 'active' }"
            @click="activeTab = 'active'"
            type="button"
          >
            Active Users ({{ totalActive }})
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link manage-users-tab text-16"
            :class="{ active: activeTab === 'pending' }"
            @click="activeTab = 'pending'"
            type="button"
          >
            Pending Invites ({{ totalPending }})
          </button>
        </li>
      </ul>
    </div>

      <!-- Active Users Tab -->
      <div v-if="activeTab === 'active'">
        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        <table v-else class="table manage-users-table align-middle mb-0">
          <colgroup>
            <col style="width: 25%" />
            <col style="width: 25%" />
            <col style="width: 14%" />
            <col style="width: 30%" />
            <col style="width: 6%" />
          </colgroup>
          <thead>
            <tr class="text-16">
              <th class="fw-bold py-3 ps-4">User Name</th>
              <th class="fw-bold py-3">Title</th>
              <th class="fw-bold py-3 text-center">Employers</th>
              <th class="fw-bold py-3 text-center">Account Status</th>
              <th class="fw-bold py-3 text-center" style="width: 80px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in activeUsers" :key="user.id" class="text-16">
              <td class="ps-4">{{ user.fullname }}</td>
              <td>{{ user.role || 'N/a' }}</td>
              <td class="text-center">
                <BcsStatPill :value="user.employerCount" size="sm" />
              </td>
              <td class="text-center text-14">
                <BcsStatusPill text="Active" color="success" :bold="false"/>
              </td>
              <td class="text-center">
                <BcsDropdownActions
                  :item-id="user.id"
                  :actions="getDropdownActions(user)"
                  @action-click="onDropdownAction($event, user)"
                />
              </td>
            </tr>
            <tr v-if="activeUsers.length === 0 && !isLoading">
              <td colspan="5" class="text-center text-muted py-3">No active users found.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pending Invites Tab -->
      <div v-else-if="activeTab === 'pending'">
        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        <table v-else class="table manage-users-table align-middle mb-0">
            <colgroup>
              <col style="width: 25%" />
              <col style="width: 25%" />
              <col style="width: 14%" />
              <col style="width: 30%" />
              <col style="width: 6%" />
            </colgroup>
          <thead>
            <tr class="text-16">
              <th class="fw-bold py-3 ps-4">User Name</th>
              <th class="fw-bold py-3">Title</th>
              <th class="fw-bold py-3">Invite Sent</th>
              <th class="fw-bold py-3 text-center">Account Status</th>
              <th class="fw-bold py-3 text-center" style="width: 50px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in pendingUsers" :key="user.id" class="text-16">
              <td class="ps-4">{{ user.fullname }}</td>
              <td>{{ user.role || 'N/a' }}</td>
              <td>{{ formatDate(user.lastInviteSentAt || user.authCreatedAt) }}</td>
              <td class="text-center">
                <BcsStatusPill text="Invite Pending" color="warning" :bold="false" />
              </td>
              <td class="text-center">
                <BcsDropdownActions
                  :item-id="user.id"
                  :actions="getPendingDropdownActions(user)"
                  @action-click="onDropdownAction($event, user)"
                />
              </td>
            </tr>
            <tr v-if="pendingUsers.length === 0 && !isLoading">
              <td colspan="5" class="text-center text-muted py-3">No pending invites found.</td>
            </tr>
          </tbody>
        </table>
      </div>

    <AddAccountUserModal
      ref="addUserModalRef"
      @submit="onInviteSubmit"
    />

    <ConfirmActionModal
      ref="confirmModalRef"
      :title="confirmModalTitle"
      :body="confirmModalBody"
      :confirmLabel="confirmModalLabel"
      :isProcessing="isConfirmProcessing"
      @confirm="onConfirmAction"
    />

    <RemoveUserModal
      ref="removeUserModalRef"
      :user="userToRemove"
      :activeUsers="activeUsers"
      @confirm="onRemoveConfirm"
    />
  </BcsCard>

  <!-- Load More (outside card) -->
  <div v-if="activeTab === 'active' && !isLoading && activeUsers.length > 0" class="text-start mt-3">
    <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
      You're viewing {{ activeUsers.length > totalActive ? totalActive : activeUsers.length }} of {{ totalActive }} active users
    </p>
    <button
      v-if="hasMoreActive"
      type="button"
      class="btn btn-info text-16 mt-2"
      :disabled="isLoadingMoreActive"
      @click="adminCenterStore.loadMoreActiveUsers()"
    >
      <med>Load More Users</med>
      <i v-if="!isLoadingMoreActive" class="bi bi-chevron-double-down ms-2"></i>
      <span v-if="isLoadingMoreActive" class="spinner-border spinner-border-sm ms-2" role="status"></span>
    </button>
  </div>
  <div v-if="activeTab === 'pending' && !isLoading && pendingUsers.length > 0" class="text-start mt-3">
    <p class="text-14 mb-0" style="color: var(--bs-gray-700)">
      You're viewing {{ pendingUsers.length > totalPending ? totalPending : pendingUsers.length }} of {{ totalPending }} pending invites
    </p>
    <button
      v-if="hasMorePending"
      type="button"
      class="btn btn-info text-16 mt-2"
      :disabled="isLoadingMorePending"
      @click="adminCenterStore.loadMorePendingUsers()"
    >
      <med>Load More Users</med>
      <i v-if="!isLoadingMorePending" class="bi bi-chevron-double-down ms-2"></i>
      <span v-if="isLoadingMorePending" class="spinner-border spinner-border-sm ms-2" role="status"></span>
    </button>
  </div>

</template>

<style scoped lang="scss">
:deep(.card-body) {
  padding: 0;
}

.manage-users-tab {
  color: var(--bs-primary) !important;
  font-weight: 500 !important;
  border: 1px solid var(--bs-border-color);
  border-bottom: none;
  background-color: transparent;

  &.active {
    color: var(--bs-secondary) !important;
    background-color: var(--bs-white);
    border-bottom-color: var(--bs-white);    
  }

  &:hover:not(.active) {
    color: var(--bs-primary);
  }
}

.manage-users-table {
  border-collapse: collapse;

  th, td {
    border-left: none;
    border-right: none;
  }

  thead th {
    background-color: var(--bs-white);
    border-bottom: 2px solid var(--bs-border-color);
    border-top: 1px solid var(--bs-border-color);
  }

  tbody td {
    border-bottom: 1px solid var(--bs-border-color);
  }

  tbody tr:last-child td {
    border-bottom: 1px solid var(--bs-border-color);
  }
}

.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
